// src/hooks/useAuth.ts
import { useEffect, useState, useRef, useCallback } from 'react';
import { User, Session, AuthChangeEvent } from '@supabase/supabase-js';
import { useNavigate } from 'react-router-dom';
import { supabase, isSupabaseConfigured } from '../utils/supabaseClient';
import { createCompanyOnSignup } from '../utils/createCompanyOnSignup';

interface Profile {
  id: string;
  full_name: string | null;
  email: string | null;
  role: 'builder' | 'vendor' | 'admin' | 'government';
  company_name: string | null;
  license_number: string | null;
  phone_number: string | null;
  preferred_trade: string | null;
  preferred_area_code: string | null;  // Added for phone provisioning
  avatar_url: string | null;
  team_id: string | null;  // Added for team association
  created_at: string;
  updated_at: string;
}

// Profile cache to prevent multiple fetches
const profileCache = new Map<string, Profile>();
const profileFetchPromises = new Map<string, Promise<Profile | null>>();

export function useAuth() {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  
  // Track if we've already initialized to prevent duplicate calls
  const initializationRef = useRef(false);
  const lastFetchedUserId = useRef<string | null>(null);
  const mounted = useRef(true);

  // Memoized profile fetcher with caching and deduplication
  const fetchProfile = useCallback(async (userId: string): Promise<Profile | null> => {
    // Check cache first
    if (profileCache.has(userId)) {
      const cached = profileCache.get(userId)!;
      console.log('✓ Using cached profile for user:', userId);
      return cached;
    }

    // Check if there's already a fetch in progress for this user
    if (profileFetchPromises.has(userId)) {
      console.log('⏳ Waiting for existing profile fetch for user:', userId);
      try {
        // Wait for the existing promise with a timeout
        const existingPromise = profileFetchPromises.get(userId)!;
        const timeoutPromise = new Promise<Profile | null>((_, reject) =>
          setTimeout(() => reject(new Error('Profile fetch timeout')), 10000)
        );
        return await Promise.race([existingPromise, timeoutPromise]);
      } catch (error) {
        console.warn('⚠️ Existing fetch timed out or failed, starting new fetch');
        profileFetchPromises.delete(userId);
        // Fall through to create new fetch
      }
    }

    // Create new fetch promise
    const fetchPromise = (async () => {
      try {
        console.log('🔍 Fetching profile for user:', userId);
        
        if (!isSupabaseConfigured() || !supabase) {
          // Return mock profile if Supabase not configured
          const mockProfile: Profile = {
            id: userId,
            email: 'user@example.com',
            full_name: 'Test User',
            role: 'builder',
            company_name: null,
            license_number: null,
            phone_number: null,
            preferred_trade: null,
            preferred_area_code: null,
            avatar_url: null,
            team_id: null,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          };
          profileCache.set(userId, mockProfile);
          return mockProfile;
        }

        const { data, error } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', userId)
          .maybeSingle();

        if (error) {
          // Handle 406 error (RLS policy rejection)
          if (error.code === '406' || error.message?.includes('406')) {
            console.warn('Profile fetch rejected by RLS policy, attempting to create profile...');
            // Fall through to create profile logic
          }

          // Profile might not exist yet, create it
          if (error.code === 'PGRST116' || error.code === '406' || !data) {
            console.log('Profile not found, creating...');
            const { data: userData } = await supabase.auth.getUser();
            
            if (userData?.user) {
              const newProfile: Profile = {
                id: userId,
                email: userData.user.email || null,
                full_name: userData.user.user_metadata?.full_name || null,
                role: (userData.user.user_metadata?.role as 'builder' | 'vendor') || 'builder',
                company_name: userData.user.user_metadata?.company_name || null,
                license_number: null,
                phone_number: userData.user.user_metadata?.phone || null,
                preferred_trade: null,
                preferred_area_code: userData.user.user_metadata?.preferred_area_code || null,
                avatar_url: null,
                team_id: null,
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString()
              };
              
              // Use upsert to avoid conflicts
              const { data: upsertedProfile, error: upsertError } = await supabase
                .from('profiles')
                .upsert(newProfile, { onConflict: 'id' })
                .select()
                .single();

              if (!upsertError && upsertedProfile) {
                // Create company/team for new user (with timeout to prevent hanging)
                console.log('🏢 Creating company for new user...');
                try {
                  const companyPromise = createCompanyOnSignup(
                    userId,
                    userData.user.email || '',
                    userData.user.user_metadata?.full_name,
                    userData.user.user_metadata?.company_name
                  );
                  const timeoutPromise = new Promise((_, reject) =>
                    setTimeout(() => reject(new Error('Company creation timeout')), 8000)
                  );
                  await Promise.race([companyPromise, timeoutPromise]);
                  console.log('✓ Company created successfully');
                } catch (companyError) {
                  console.warn('⚠️ Company creation failed or timed out:', companyError);
                  // Continue anyway - user can create company later
                }

                profileCache.set(userId, upsertedProfile as Profile);
                return upsertedProfile as Profile;
              }

              // Fallback to the newProfile if insert/fetch failed
              profileCache.set(userId, newProfile);
              return newProfile;
            }
          } else {
            console.error('Error fetching profile:', error);
          }
          
          // Return minimal profile on error
          const minimalProfile: Profile = {
            id: userId,
            email: null,
            full_name: null,
            role: 'builder',
            company_name: null,
            license_number: null,
            phone_number: null,
            preferred_trade: null,
            preferred_area_code: null,
            avatar_url: null,
            team_id: null,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          };
          
          profileCache.set(userId, minimalProfile);
          return minimalProfile;
        }

        console.log('✓ Profile fetched successfully');
        const fetchedProfile = data as Profile;

        // Check if profile has team_id, if not create team
        if (!fetchedProfile.team_id) {
          console.log('⚠️ Profile missing team_id, creating team...');
          try {
            const { data: userData } = await supabase.auth.getUser();
            if (userData?.user) {
              const companyPromise = createCompanyOnSignup(
                userId,
                userData.user.email || fetchedProfile.email || '',
                userData.user.user_metadata?.full_name || fetchedProfile.full_name,
                userData.user.user_metadata?.company_name || fetchedProfile.company_name
              );
              const timeoutPromise = new Promise((_, reject) =>
                setTimeout(() => reject(new Error('Company creation timeout')), 8000)
              );
              await Promise.race([companyPromise, timeoutPromise]);
              console.log('✓ Team created for existing profile');

              // Refetch profile to get updated team_id
              const { data: updatedProfile } = await supabase
                .from('profiles')
                .select('*')
                .eq('id', userId)
                .single();

              if (updatedProfile) {
                profileCache.set(userId, updatedProfile as Profile);
                return updatedProfile as Profile;
              }
            }
          } catch (companyError) {
            console.warn('⚠️ Team creation failed:', companyError);
            // Continue with profile without team
          }
        }

        profileCache.set(userId, fetchedProfile);
        return fetchedProfile;
        
      } catch (error) {
        console.error('Profile fetch error:', error);
        return null;
      } finally {
        // Clean up the promise from the map
        profileFetchPromises.delete(userId);
      }
    })();

    // Store the promise to prevent duplicate fetches
    profileFetchPromises.set(userId, fetchPromise);
    return fetchPromise;
  }, []);

  useEffect(() => {
    mounted.current = true;

    // Prevent multiple initializations
    if (initializationRef.current) {
      return;
    }
    initializationRef.current = true;

    const initializeAuth = async () => {
      try {
        if (!isSupabaseConfigured() || !supabase) {
          console.error('Supabase not configured - check environment variables');
          setLoading(false);
          return;
        }

        // Get the session without timeout - always fetch fresh to avoid wrong user
        const sessionStartTime = Date.now();

        // Fetch fresh session from Supabase (don't use cache - it can have wrong user)
        const { data: { session }, error } = await supabase.auth.getSession();
        console.log(`Auth session fetch completed in ${Date.now() - sessionStartTime}ms`);

        if (!mounted.current) return;

        if (error) {
          console.error('Session error:', error);
          setLoading(false);
        } else if (session?.user) {
          console.log('User session found:', session.user.email);
          setUser(session.user);
          
          // Fetch profile only if we haven't already
          if (lastFetchedUserId.current !== session.user.id) {
            lastFetchedUserId.current = session.user.id;
            const fetchedProfile = await fetchProfile(session.user.id);
            if (mounted.current && fetchedProfile) {
              setProfile(fetchedProfile);
            }
          }
          
          setLoading(false);
        } else {
          console.log('No active session');
          setLoading(false);
        }
        
      } catch (error) {
        console.error('Auth initialization error:', error);
        if (mounted.current) {
          setLoading(false);
        }
      }
    };

    // Start initialization
    initializeAuth();

    // Listen for auth changes
    let subscription: any;
    
    if (isSupabaseConfigured() && supabase) {
      const { data } = supabase.auth.onAuthStateChange(
        async (event: AuthChangeEvent, session: Session | null) => {
          // Skip if this is the initial session (already handled above)
          if (event === 'INITIAL_SESSION') {
            return;
          }
          
          console.log('Auth state changed:', event, session?.user?.email);

          if (!mounted.current) return;

          if (session?.user) {
            setUser(session.user);
            setLoading(false);
            
            // Only fetch profile if user changed
            if (lastFetchedUserId.current !== session.user.id) {
              lastFetchedUserId.current = session.user.id;
              const fetchedProfile = await fetchProfile(session.user.id);
              if (mounted.current && fetchedProfile) {
                setProfile(fetchedProfile);
              }
            }
          } else {
            setUser(null);
            setProfile(null);
            lastFetchedUserId.current = null;
            setLoading(false);
          }
        }
      );
      subscription = data.subscription;
    }

    // Cleanup
    return () => {
      mounted.current = false;
      subscription?.unsubscribe();
    };
  }, [fetchProfile]);

  const signIn = async ({ email, password }: { email: string; password: string }) => {
    try {
      if (!isSupabaseConfigured() || !supabase) {
        // Mock sign in
        console.log('Mock sign in:', email);
        return { data: null, error: null };
      }

      // Use API endpoint for sign in (this will be called from LoginPage)
      // This function is kept for compatibility but the actual signin happens in LoginPage
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        console.error('Sign in error:', error);
        return { data: null, error };
      }

      console.log('Sign in successful:', email);
      return { data, error: null };
      
    } catch (error) {
      console.error('Sign in exception:', error);
      return { data: null, error };
    }
  };

  const signUp = async ({
    email,
    password,
    fullName,
    role = 'builder',
    companyName,
    phone,
    preferredAreaCode
  }: {
    email: string;
    password: string;
    fullName: string;
    role?: 'builder' | 'vendor' | 'admin' | 'government';
    companyName?: string;
    phone?: string;
    preferredAreaCode?: string;
  }) => {
    try {
      if (!isSupabaseConfigured() || !supabase) {
        // Mock sign up
        console.log('Mock sign up:', email);
        return { data: null, error: null };
      }

      // This is kept for compatibility, but the actual signup now happens in LoginPage
      // using the API endpoint which properly stores company name
      const redirectUrl = `${window.location.origin}/dashboard`;

      const { data: authData, error: authError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: redirectUrl,
          data: {
            full_name: fullName,
            company_name: companyName,
            role: role,
            phone: phone,
            preferred_area_code: preferredAreaCode,
          }
        }
      });

      if (authError) {
        console.error('Sign up error:', authError);
        return { data: null, error: authError };
      }

      console.log('Sign up successful, check email for verification');

      // Auto-create company/team for the new user
      if (authData?.user) {
        console.log('🏢 Attempting to create company for user:', authData.user.id);
        try {
          const team = await createCompanyOnSignup(authData.user.id, email, fullName, companyName);
          if (team) {
            console.log('✅ Company/team created successfully:', team.id);
          } else {
            console.warn('⚠️ Company creation returned null - check RLS policies');
          }
          // Note: Trial subscription will be created after Stripe payment in PaymentSuccessPage
        } catch (teamError: any) {
          console.error('❌ Error creating company:', {
            message: teamError.message,
            code: teamError.code,
            details: teamError.details,
            hint: teamError.hint
          });
          // Don't fail signup if company creation fails
        }
      }

      return { data: authData, error: null };
      
    } catch (error) {
      console.error('Sign up exception:', error);
      return { data: null, error };
    }
  };

  const signOut = async () => {
    try {
      // Clear cached profile
      if (user) {
        profileCache.delete(user.id);
        lastFetchedUserId.current = null;
      }

      // Clear state
      setUser(null);
      setProfile(null);

      // Clear all localStorage data to force fresh profile load
      localStorage.clear();
      sessionStorage.clear();

      if (isSupabaseConfigured() && supabase) {
        const { error } = await supabase.auth.signOut();
        if (error) {
          console.error('Sign out error:', error);
          return { error };
        }
      }

      navigate('/login');
      return { error: null };

    } catch (error) {
      console.error('Sign out exception:', error);
      return { error };
    }
  };

  const updateProfile = async (updates: Partial<Profile>) => {
    if (!user) {
      return { error: new Error('No user logged in') };
    }

    try {
      if (!isSupabaseConfigured() || !supabase) {
        // Mock update
        setProfile(prev => prev ? { ...prev, ...updates } : null);
        return { error: null };
      }

      const { error } = await supabase
        .from('profiles')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', user.id);

      if (error) throw error;

      // Update cache
      const updatedProfile = { ...profile!, ...updates };
      profileCache.set(user.id, updatedProfile);
      setProfile(updatedProfile);

      return { error: null };
    } catch (error) {
      console.error('Profile update error:', error);
      return { error };
    }
  };

  // Clear profile cache on unmount or when user changes
  useEffect(() => {
    return () => {
      if (user) {
        // Optional: clear cache for this user when component unmounts
        // profileCache.delete(user.id);
      }
    };
  }, [user]);

  return { 
    user, 
    profile,
    loading,
    signIn,
    signUp,
    signOut,
    updateProfile,
    isAuthenticated: !!user,
    isUsingMockAuth: !isSupabaseConfigured()
  };
}